import { lazy, ComponentType } from 'react';
import { SESSION_STORAGE_KEY } from '../constant/auth.constant';

/**
 * Key used in sessionStorage to track whether we've already attempted
 * a reload for a specific chunk that failed to load.
 */
const RETRY_STORAGE_KEY = SESSION_STORAGE_KEY.CHUNK_RETRY_RELOADED;

/**
 * Detects whether an error is a chunk/module load failure.
 * This covers Vite's dynamic import errors and webpack chunk loading errors.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    (error.name === 'TypeError' && message.includes('failed to fetch'))
  );
};

/**
 * Wraps a dynamic import function with retry logic for chunk loading failures.
 *
 * After a new deployment, Vite generates new hashed chunk filenames.
 * Users with cached HTML still reference old chunk URLs that no longer exist
 * on the server, causing "Failed to fetch dynamically imported module" errors.
 *
 * This utility:
 * 1. Attempts the dynamic import
 * 2. If it fails with a chunk load error, forces a full page reload (once)
 *    to fetch the latest HTML with correct chunk references
 * 3. Uses sessionStorage to prevent infinite reload loops
 *
 * @param importFn - A function that returns a dynamic import promise
 * @param retryKey - Optional unique key to track retries per route/module
 * @returns A React.lazy-compatible component
 *
 * @example
 * // Instead of:
 * const Dashboard = lazy(() => import('../pages/Dashboard'));
 *
 * // Use:
 * const Dashboard = lazyRetry(() => import('../pages/Dashboard'));
 */
export const lazyRetry = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retryKey?: string,
): React.LazyExoticComponent<T> => {
  return lazy(() => retryDynamicImport(importFn, retryKey));
};

/**
 * Wraps a dynamic import with chunk-load retry logic.
 * Can be used directly with React.lazy() or with named exports.
 *
 * @example
 * // Named export usage:
 * const Dashboard = lazy(() =>
 *   retryDynamicImport(
 *     () => import('../pages').then(m => ({ default: m.Dashboard })),
 *     'dashboard'
 *   )
 * );
 */
export const retryDynamicImport = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retryKey?: string,
): Promise<{ default: T }> => {
  return importFn().catch((error: unknown) => {
    // Only handle chunk load errors
    if (!isChunkLoadError(error)) {
      throw error;
    }

    const storageKey = retryKey
      ? `${RETRY_STORAGE_KEY}_${retryKey}`
      : RETRY_STORAGE_KEY;

    const hasReloaded = sessionStorage.getItem(storageKey);

    if (!hasReloaded) {
      // Mark that we're about to reload for this chunk
      sessionStorage.setItem(storageKey, 'true');

      // Force a full page reload to get fresh HTML with new chunk URLs
      window.location.reload();

      // Return a never-resolving promise to prevent rendering while reloading
      return new Promise<{ default: T }>(() => {});
    }

    // Already reloaded once — clear the flag and let the error boundary handle it
    sessionStorage.removeItem(storageKey);
    throw error;
  });
};

/**
 * Clears all chunk retry flags from sessionStorage.
 * Call this on successful app load to reset retry state.
 */
export const clearChunkRetryFlags = (): void => {
  const keysToRemove: string[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(RETRY_STORAGE_KEY)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
};
